import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useLanguage, useTheme, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sun, Moon, Globe, ArrowLeft, Lock, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const ResetPassword = () => {
  const { t, language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }

    // Verify token on mount
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error(language === "it" ? "La password deve avere almeno 6 caratteri" : "Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error(language === "it" ? "Le password non coincidono" : "Passwords do not match");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token,
        new_password: password 
      });
      setSuccess(true);
      toast.success(language === "it" ? "Password reimpostata con successo!" : "Password reset successfully!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 
        (language === "it" ? "Errore nel reset della password" : "Password reset error");
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {language === "it" ? "Verifica in corso..." : "Verifying..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
                alt="SmartBooker" 
                className="h-8 w-auto"
              />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={() => changeLanguage(language === "it" ? "en" : "it")}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                <Globe className="w-5 h-5" />
              </button>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center pt-16 px-4">
        <div className="w-full max-w-md">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === "it" ? "Torna al login" : "Back to login"}
          </Link>

          <div className="p-8 rounded-2xl border bg-card shadow-card">
            {!token || !tokenValid ? (
              // Invalid or missing token
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold mb-2">
                  {language === "it" ? "Link Non Valido" : "Invalid Link"}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {language === "it" 
                    ? "Il link per il reset della password non è valido o è scaduto. Richiedi un nuovo link."
                    : "The password reset link is invalid or has expired. Please request a new link."
                  }
                </p>
                <Link to="/forgot-password">
                  <Button className="rounded-full">
                    {language === "it" ? "Richiedi Nuovo Link" : "Request New Link"}
                  </Button>
                </Link>
              </div>
            ) : success ? (
              // Success state
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-semibold mb-2">
                  {language === "it" ? "Password Reimpostata!" : "Password Reset!"}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {language === "it" 
                    ? "La tua password è stata reimpostata con successo. Ora puoi accedere con la nuova password."
                    : "Your password has been reset successfully. You can now login with your new password."
                  }
                </p>
                <Link to="/login">
                  <Button className="rounded-full">
                    {language === "it" ? "Vai al Login" : "Go to Login"}
                  </Button>
                </Link>
              </div>
            ) : (
              // Reset form
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-accent" />
                  </div>
                  <h1 className="text-2xl font-serif font-medium">
                    {language === "it" ? "Nuova Password" : "New Password"}
                  </h1>
                  <p className="mt-2 text-muted-foreground">
                    {language === "it" 
                      ? `Inserisci una nuova password per ${email}`
                      : `Enter a new password for ${email}`
                    }
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      {language === "it" ? "Nuova Password" : "New Password"}
                    </label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={language === "it" ? "Minimo 6 caratteri" : "Minimum 6 characters"}
                      required
                      data-testid="new-password-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      {language === "it" ? "Conferma Password" : "Confirm Password"}
                    </label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={language === "it" ? "Ripeti la password" : "Repeat password"}
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-full"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                  >
                    {loading 
                      ? (language === "it" ? "Salvataggio..." : "Saving...") 
                      : (language === "it" ? "Reimposta Password" : "Reset Password")
                    }
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
